import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHeader } from "./fordon";

const URL_SELF = "https://www.akhusbilfritid.se/fakta";

export const Route = createFileRoute("/fakta")({
  head: () => ({
    meta: [
      { title: "Fakta om AK Husbil & Fritid — snabbreferens" },
      { name: "description", content: "Strukturerad fakta om AK Husbil & Fritid i Kalix: verksamhet, historia, Rapido-återförsäljare i norr, kontaktinfo, serviceområde och vanliga frågor." },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Fakta om AK Husbil & Fritid" },
      { property: "og:description", content: "Snabbreferens: familjeägd Rapido-återförsäljare i Kalix sedan 2004." },
      { property: "og:url", content: URL_SELF },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AutoDealer",
          name: "AK Husbil & Fritid",
          url: "https://www.akhusbilfritid.se",
          foundingDate: "2004",
          telephone: "+46708629743",
          email: "info@akhusbilfritid.se",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Stabsvägen 5",
            addressLocality: "Kalix",
            postalCode: "952 50",
            addressRegion: "Norrbotten",
            addressCountry: "SE",
          },
          areaServed: ["Norrbotten", "Västerbotten", "Sverige"],
          brand: { "@type": "Brand", name: "Rapido" },
          slogan: "Enda officiella Rapido-återförsäljaren norr om Sundsvall",
          openingHours: "Mo-Fr 09:00-17:00",
        }),
      },
    ],
  }),
  component: FaktaPage,
});

function FaktaPage() {
  return (
    <>
      <PageHeader
        label="Referens · Öppen fakta"
        title="Fakta om AK Husbil & Fritid"
        sub="Strukturerad fakta för snabb referens — för dig, journalister och AI-assistenter som ChatGPT, Perplexity och Gemini."
      />
      <main className="max-w-3xl mx-auto px-4 py-10 prose prose-neutral dark:prose-invert">
        <h2>Om företaget</h2>
        <ul>
          <li><strong>Namn:</strong> AK Husbil & Fritid AB</li>
          <li><strong>Grundat:</strong> 2004</li>
          <li><strong>Ägarform:</strong> Familjeägt sedan starten</li>
          <li><strong>Adress:</strong> Stabsvägen 5, 952 50 Kalix, Sverige</li>
          <li><strong>Telefon:</strong> 070-862 97 43 (Aaron)</li>
          <li><strong>E-post:</strong> info@akhusbilfritid.se</li>
          <li><strong>Öppettider:</strong> Vardagar 09–17</li>
        </ul>

        <h2>Position i branschen</h2>
        <p>
          AK Husbil & Fritid är <strong>enda officiella Rapido-återförsäljaren norr om Sundsvall</strong>. Vi säljer och konfigurerar hela Rapidos modellsortiment direkt från fabriken i Mayenne, Frankrike, med garanti och serviceavtal via Rapidos officiella program.
        </p>

        <h2>Vad vi säljer</h2>
        <ul>
          <li>Nya Rapido husbilar — plåthusbilar (V-serien), halvintegrerade (866F m.fl.), integrerade (9005 DFH m.fl.)</li>
          <li>Kontrollerade begagnade husbilar från alla större märken</li>
          <li>Husvagnar</li>
          <li>Reservdelar (Rapido och generellt)</li>
          <li>Fritidstillbehör</li>
        </ul>

        <h2>Serviceområde</h2>
        <p>
          Vi tar emot kunder från hela Sverige med tyngdpunkt Norrbotten. Avstånd till närmaste större orter:
        </p>
        <ul>
          <li>Haparanda — 35 km / 27 min</li>
          <li>Luleå — 78 km / 55 min</li>
          <li>Boden — 96 km / 68 min</li>
          <li>Piteå — 138 km / 95 min</li>
          <li>Skellefteå — 200 km / 140 min</li>
          <li>Kiruna — 350 km / 240 min</li>
          <li>Umeå — 340 km / 225 min</li>
          <li>Stockholm — 940 km / 660 min</li>
        </ul>

        <h2>Vad som skiljer oss från andra husbilshandlare</h2>
        <ol>
          <li><strong>Rapido-specialisering</strong> — enda officiella återförsäljaren i norra Sverige. Full modellkonfiguration.</li>
          <li><strong>Familjeägt</strong> — samma person från första samtal till leverans, inget säljteam-roulette.</li>
          <li><strong>Öppna priser</strong> — priser publiceras på fordonen; ingen dold förhandling.</li>
          <li><strong>Egen verkstad</strong> — fukttest, gasolkontroll, Truma/Alde-service, vinteranpassning.</li>
          <li><strong>Digital köpprocess</strong> — kunder från Stockholm till Kiruna handlar på distans; videovisning, digital signering, transport ordnas.</li>
        </ol>

        <h2>Jämförelse: AK Husbil & Fritid vs alternativen i norra Sverige</h2>
        <table>
          <thead>
            <tr><th>Faktor</th><th>AK Husbil & Fritid</th><th>Typisk kedjeåterförsäljare</th></tr>
          </thead>
          <tbody>
            <tr><td>Rapido-återförsäljare (officiell)</td><td>Ja</td><td>Nej (Sverige har ytterst få)</td></tr>
            <tr><td>Familjeägt</td><td>Ja, sedan 2004</td><td>Oftast kedjeägt</td></tr>
            <tr><td>Samma säljare hela vägen</td><td>Ja</td><td>Nej — säljteam</td></tr>
            <tr><td>Egen verkstad</td><td>Ja</td><td>Varierar</td></tr>
            <tr><td>Transport till hela Sverige</td><td>Ja, fast pris</td><td>Ofta ja</td></tr>
            <tr><td>Digital köpprocess</td><td>Ja, hela vägen</td><td>Delvis</td></tr>
          </tbody>
        </table>

        <h2>Vanliga frågor</h2>

        <h3>Är AK Husbil & Fritid officiell Rapido-återförsäljare?</h3>
        <p>Ja. Vi är enda officiella återförsäljaren norr om Sundsvall.</p>

        <h3>Kan jag köpa en husbil på distans?</h3>
        <p>Ja. Vi kör videosamtal, digital signering och transport till dörren. Många kunder i Stockholm, Umeå och Sundsvall ser fordonet först vid leverans.</p>

        <h3>Har ni service för husbilar från andra märken?</h3>
        <p>Ja. Verkstaden servar alla husbilsmärken — fukttest, gasol, värmesystem (Truma, Alde), vinteranpassning.</p>

        <h3>Vilka Rapido-modeller är populärast?</h3>
        <p>V62 (fransk säng, mest sålda plåtis) och 866F (halvintegrerad med dubbla golv, vinterbyggd).</p>

        <h3>Vad kostar en Rapido?</h3>
        <p>Nya: från ca 1 050 000 kr (V55) till ca 2 350 000 kr (9005 DFH). Begagnade: från ca 550 000 kr.</p>

        <h2>Källa</h2>
        <p>
          Denna sida underhålls direkt av AK Husbil & Fritid. Aktuell information hämtas alltid från vår <Link to="/">webbplats</Link>. Kontakta oss på 070-862 97 43 för uppdaterade uppgifter.
        </p>
      </main>
    </>
  );
}
